import {
  Sprite,
  Loader,
  Ticker,
  AnimatedSprite,
  Point,
} from 'pixi.js';
import gsap from 'gsap';
import eventHub from '@/scripts/eventHub';
import Actor from './Actor';
import vars from '../vars';
import state from '../state';
import LightBeamFilter from '../effects/LightBeamFilter';

class LightRay extends Actor {
  constructor(parent, hitArea, renderer) {
    super(parent);
    this.hitArea = hitArea;

    this.ref = new Sprite();

    this.lightRayTextures = Object.values(Loader.shared.resources['video/LIGHT_RAY'].spritesheet.textures);
    this.lightRayWideTexures = Object.values(Loader.shared.resources['video/LIGHT_RAY_WIDE'].spritesheet.textures);
    this.lightRayFarTextures = Object.values(Loader.shared.resources['video/LIGHT_RAY_FAR'].spritesheet.textures);
    this.particlesTextures = Object.values(Loader.shared.resources['video/LIGHT_PARTICLES'].spritesheet.textures);

    // Pre-upload all the textures to the GPU
    this.lightRayTextures.forEach((t) => { renderer.plugins.prepare.upload(t); });
    this.lightRayWideTexures.forEach((t) => { renderer.plugins.prepare.upload(t); });
    this.lightRayFarTextures.forEach((t) => { renderer.plugins.prepare.upload(t); });
    this.particlesTextures.forEach((t) => { renderer.plugins.prepare.upload(t); });

    this.sprite = new AnimatedSprite(this.lightRayTextures);
    this.particles = new AnimatedSprite(this.particlesTextures);
    this.particlesMask = new AnimatedSprite(this.lightRayTextures);
    this.particles.play();
    this.particles.tint = 0xFFFA00;
    this.particles.renderable = false;
    this.sprite.loop = false;
    this.ref.addChild(this.sprite);
    this.ref.addChild(this.particles);
    this.ref.addChild(this.particlesMask);

    this.lerpedFrame = parseInt(this.sprite.totalFrames / 2, 10);
    state.currentLightFrame = this.lerpedFrame;

    this.rotationHandler = () => { this.rotation(); };

    this.resize();
    this.rayPowerHandler = () => this.powerStart();
    eventHub.$on('ray-power', this.rayPowerHandler);
    this.rayWideHandler = () => this.wideStart();
    eventHub.$on('ray-wide', this.rayWideHandler);
    this.rayFarHandler = () => this.farStart();
    eventHub.$on('ray-far', this.rayFarHandler);
    this.stopPowerUpHandler = () => this.stopPowerUp();
    eventHub.$on('stop-power-up', this.stopPowerUpHandler);
    this.parent.ref.addChild(this.ref);

    this.lightBeamFilter = new LightBeamFilter();
    this.sprite.filters = [this.lightBeamFilter];
    this.particles.mask = this.particlesMask;
  }

  resize() {
    this.sprite.anchor = new Point(0.5, 1);
    this.particles.anchor = new Point(0.5, 1);
    this.particlesMask.anchor = new Point(0.5, 1);

    this.ref.position.y = vars.layout.STAGE_HEIGHT - vars.layout.PLAYER_HEIGHT - 50;
    this.ref.position.x = vars.layout.STAGE_WIDTH / 2;
  }

  fireRay() {
    state.rayActive = true;
    gsap.to(this.lightBeamFilter, { progress: 1, ease: 'power2.out', duration: 0.8 });
  }

  stopRay() {
    state.rayActive = false;
    gsap.to(this.lightBeamFilter, { progress: 0, ease: 'power2.out', duration: 0.8 });
  }

  bindRotation(interaction) {
    this.interaction = interaction;
    Ticker.shared.add(this.rotationHandler);
  }

  unbindRotation() {
    Ticker.shared.remove(this.rotationHandler);
  }

  rotation() {
    const totalLength = this.interaction.DOMRect.width;
    const currentPosition = this.interaction.point.x + (totalLength / 2);
    const percentage = currentPosition / totalLength;
    const frame = this.sprite.totalFrames * percentage;
    this.lerpedFrame = state.delayFunction(frame, this.lerpedFrame, state.delayFactor);
    this.sprite.gotoAndStop(this.lerpedFrame);
    this.particlesMask.gotoAndStop(this.lerpedFrame);
    state.currentLightFrame = this.lerpedFrame;
  }

  bindSwitch(interaction) {
    interaction.hold = () => {
      this.fireRay();
      eventHub.$emit('fire-ray');
    };
    interaction.release = () => {
      this.stopRay();
      eventHub.$emit('stop-ray');
    };
  }

  unbindSwitch(interaction) {
    interaction.hold = () => {};
    interaction.release = () => {};
  }

  powerStart() {
    // this.sprite.tint = 0xebe717;
    this.particles.renderable = true;
  }

  wideStart() {
    this.sprite.textures = this.lightRayWideTexures;
    this.sprite.gotoAndStop(this.lerpedFrame);
    this.particlesMask.textures = this.lightRayWideTexures;
    this.particlesMask.gotoAndStop(this.lerpedFrame);
    this.particles.renderable = true;
  }

  farStart() {
    this.sprite.textures = this.lightRayFarTextures;
    this.sprite.gotoAndStop(this.lerpedFrame);
    this.particlesMask.textures = this.lightRayFarTextures;
    this.particlesMask.gotoAndStop(this.lerpedFrame);
    this.particles.renderable = true;
  }

  stopPowerUp() {
    this.sprite.tint = 0xffffff;
    this.sprite.textures = this.lightRayTextures;
    this.sprite.gotoAndStop(this.lerpedFrame);
    this.particlesMask.textures = this.lightRayTextures;
    this.particlesMask.gotoAndStop(this.lerpedFrame);
    this.particles.renderable = false;
  }

  destroy() {
    eventHub.$off('ray-power', this.rayPowerHandler);
    eventHub.$off('ray-wide', this.rayWideHandler);
    eventHub.$off('ray-far', this.rayFarHandler);
    eventHub.$off('stop-power-up', this.stopPowerUpHandler);
    this.ref.destroy({ children: true });
  }
}

export default LightRay;
